import React, { useMemo } from 'react';
import { useForm } from '@mantine/form';
import { Button, Textarea, NumberInput, Text } from '@mantine/core';

import { Request, Store } from '../../codegen/graphql';
import MarginCalculation from './components/MarginCalculation';
import { PriceChangeRequestMetadata } from '../../types/price-change-request';
import {
    isPriceBelowCostExVat,
    BELOW_COST_WARNING,
} from '../../helpers/calculateMargin';
import { getErrorMessage } from '../../helpers/getErrorMessage';
import { getCurrencyFromRequest } from '../../utility/getProductCurrency';
import { shouldEnablePnlPriceInput } from '../../helpers/storeHelpers';
import { getRequestEffectiveCostPrice } from '../../helpers/requestZonePricing';
import { When } from '../common';
import { PriceChangeFreezeBanner } from './PriceChangeFreezeBanner';
import { useAppConfig } from '../../hooks/useAppConfig';

type Props = {
    request: Request & { resolvedVatPercentage?: number | null };
    store: Store | null;
    advertisingZoneStores: Store[];
    close: () => void;
    onConfirm: (input: {
        outcome: string;
        notes: string;
        metadata: PriceChangeRequestMetadata & {
            applyToAdvertisingZone?: boolean;
        };
    }) => void;
    showPriceChangeFreezeBanner?: boolean;
};

function AcceptPriceChangeRequestFormOps({
    request,
    store,
    close,
    onConfirm,
    showPriceChangeFreezeBanner = false,
}: Props) {
    const { vatPercentage: fallbackVatPercentage } = useAppConfig();
    const resolvedVatPercentage =
        request?.resolvedVatPercentage ?? fallbackVatPercentage;
    const highestCostPrice = getRequestEffectiveCostPrice(request);

    const priceType = useMemo(() => {
        if (store) {
            return shouldEnablePnlPriceInput(store) ? 'pnl' : 'cashbuild';
        }
        return undefined;
    }, [store]);

    const form = useForm({
        initialValues: {
            proposedPrice: request?.proposedPrice,
            notes: '',
        },
        validate: {
            proposedPrice: value => {
                if (
                    value === undefined ||
                    value === null ||
                    Number.isNaN(value) ||
                    value <= 0
                ) {
                    return 'Proposed price is required.';
                }
                return null;
            },
        },
        validateInputOnBlur: true,
        validateInputOnChange: ['proposedPrice'],
    });

    const isBelowCost = isPriceBelowCostExVat(
        form.values.proposedPrice,
        highestCostPrice,
        resolvedVatPercentage,
    );

    const handleClose = () => {
        form.reset();
        close();
    };

    const handleSubmit = form.onSubmit(values => {
        try {
            onConfirm({
                outcome: 'Approved',
                notes: values.notes,
                metadata: {
                    proposedPrice: values.proposedPrice,
                },
            });
            handleClose();
        } catch (error: unknown) {
            form.setFieldError('notes', getErrorMessage(error));
        }
    });

    const productCurrency = useMemo(
        () => getCurrencyFromRequest(request),
        [request],
    );

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-5 p-4">
            <NumberInput
                rightSection={
                    <Text className="pr-8 pl-2">{productCurrency}</Text>
                }
                label="Proposed Price"
                decimalScale={2}
                required
                min={0}
                step={0.1}
                {...form.getInputProps('proposedPrice')}
                error={
                    form.errors.proposedPrice ||
                    (isBelowCost ? BELOW_COST_WARNING : undefined)
                }
            />
            <MarginCalculation
                costPrice={highestCostPrice}
                currentSellingPrice={request?.currentPrice ?? 0}
                proposedPrice={form.values.proposedPrice}
                benchmarkMargin={request?.benchmarkMargin ?? 0}
                currency={productCurrency}
                completed={request?.completed}
                previousCostPrice={request?.previousCostPrice}
                previousSellingPrice={request?.previousSellingPrice}
                previousCashbuildPrice={request?.previousCashbuildPrice}
                previousPnlPrice={request?.previousPnlPrice}
                priceType={priceType}
                layout="row"
                vatPercentage={resolvedVatPercentage}
            />
            <When condition={showPriceChangeFreezeBanner}>
                <PriceChangeFreezeBanner />
            </When>
            <Textarea
                size="md"
                radius="lg"
                label="Notes"
                placeholder="Additional Notes"
                classNames={{
                    label: 'text-[16px]',
                }}
                {...form.getInputProps('notes')}
            />
            <div className="flex gap-4">
                <Button size="md" variant="outline" onClick={handleClose}>
                    Cancel
                </Button>
                <Button size="md" type="submit">
                    Proceed
                </Button>
            </div>
        </form>
    );
}

export default AcceptPriceChangeRequestFormOps;
